import React, { useState, useEffect, useRef } from "react";
import useIsMobile from "../hooks/useIsMobile";

import { motion, AnimatePresence } from "framer-motion";
import arrowRight from "../imgs/arrowRight.svg";
import step1 from "../imgs/step1.svg";
import step2 from "../imgs/step2.svg";
import ScrollControlledLottie from "./ScrollControlledLottie";

import "./FifthPage.css";

function FifthPage() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isComponentInView, setIsComponentInView] = useState(false);
  const sectionRefs = [useRef(null), useRef(null)];
  const componentRef = useRef(null);
  const lottieRef = useRef(null);
  const scrollCooldown = useRef(false);
  const wasInView = useRef(false);
  const lastScrollY = useRef(0);
  const { isMobile } = useIsMobile();

  const sections = [
    {
      title: (
        <div>
          <span className={`section_num ${activeIndex === 0 ? "active" : ""}`}>
            01
          </span>
          For Universal Computation: <span className="solution">Pico zkVM</span>
        </div>
      ),
      content: (
        <div className="section_content">
          <div className="des">
            The only modular zkVM achieving 99% real-time proving for current
          </div>
          <div className="des">Ethereum blocks</div>

          <div className="des" style={{ marginTop: 26 }}>
            From financial calculations to blockchain validation, Pico's modular
          </div>
          <div className="des">
            architecture integrates specialized coprocessors that deliver
          </div>

          <div className="des">
            breakthrough performance. Our distributed multi-GPU clusters prove
          </div>
          <div className="des">
            <b>45M</b> gas Ethereum blocks in <b>6.9</b> seconds on average.
            With just <b>64</b>
          </div>
          <div className="des">
            GPUs, we offer <b>3.4x</b> better performance efficiency than any
            competing
          </div>
          <div className="des">solution.</div>

          <div className="section_link_wrapper">
            <div
              className="section_link"
              onClick={() => {
                window.open("https://pico-docs.brevis.network/", "_blank");
              }}
            >
              [ Explore Pico ]
              <img
                alt=""
                className="arrow_right"
                src={arrowRight}
                onClick={() => {}}
              />
            </div>
            <div
              className="section_link"
              onClick={() => {
                window.open("https://ethproofs.org/zkvms/pico", "_blank");
              }}
            >
              [ View on Ethproofs ]
              <img
                alt=""
                className="arrow_right"
                src={arrowRight}
                onClick={() => {}}
              />
            </div>
          </div>
        </div>
      ),
    },
    {
      title: (
        <div>
          <span className={`section_num ${activeIndex === 1 ? "active" : ""}`}>
            02
          </span>
          For Blockchain Intelligence:{" "}
          <span className="solution">ZK Data Coprocessor</span>
        </div>
      ),
      content: (
        <div className="section_content">
          <div className="des">
            Powering <b>$300M+</b> in live reward programs across <b>20+</b> major
            protocols.
          </div>

          <div className="des" style={{ marginTop: 26 }}>
            Smart contracts get unlimited memory through our specialized
          </div>
          <div className="des">
            coprocessor that analyzes historical blockchain data off-chain, then
          </div>
          <div className="des">
            provides cryptographic proof of accuracy. PancakeSwap processes
          </div>
          <div className="des">
            millions of users daily with volume-based discounts. Usual
            distributes
          </div>
          <div className="des">
            rewards across 8 protocols. Linea manages 1 billion token
            distributions,
          </div>
          <div className="des">
            all with mathematical guarantees of fairness.
          </div>

          <div
            className="section_link"
            onClick={() => {
              window.open("https://coprocessor-docs.brevis.network/", "_blank");
            }}
          >
            [ Explore ZK Data Coprocessor ]
            <img
              alt=""
              className="arrow_right"
              src={arrowRight}
              onClick={() => {}}
            />
          </div>
        </div>
      ),
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        const wasInViewBefore = isComponentInView;
        setIsComponentInView(entry.isIntersecting);
        
        // When component enters view, check scroll position to determine active section
        if (entry.isIntersecting && !wasInViewBefore) {
          // Initialize lastScrollY when entering view
          lastScrollY.current = window.scrollY || window.pageYOffset;
          
          const componentRect = componentRef.current?.getBoundingClientRect();
          if (componentRect) {
            const scrollProgress = Math.abs(componentRect.top) / componentRect.height;
            // If scrolled more than 40% through the component, activate section 2
            if (scrollProgress > 0.4) {
              setActiveIndex(1);
              if (lottieRef.current) {
                lottieRef.current.playToEnd(800);
              }
            } else {
              setActiveIndex(0);
              if (lottieRef.current) {
                lottieRef.current.playToStart(800);
              }
            }
          }
          wasInView.current = true;
        } else if (!entry.isIntersecting) {
          wasInView.current = false;
        }
      },
      {
        threshold: 0.8, // Lowered threshold to make it more responsive
      }
    );

    if (componentRef.current) {
      observer.observe(componentRef.current);
    }

    return () => {
      if (componentRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        observer.unobserve(componentRef.current);
      }
    };
    // eslint-disable-next-line
  }, [isComponentInView]);

  useEffect(() => {
    const handleGlobalWheel = (e) => {
      if (!isComponentInView) return;
      if (isAnimating || scrollCooldown.current) return;
      
      const isScrollingDown = e.deltaY > 0;
      const isScrollingUp = e.deltaY < 0;
      const isAtFirstSection = activeIndex === 0;
      const isAtLastSection = activeIndex === sections.length - 1;
      let shouldSwitch = false;
      let newIndex = activeIndex;

      if (isScrollingDown && !isAtLastSection) {
        shouldSwitch = true;
        newIndex = activeIndex + 1;
      } else if (isScrollingUp && !isAtFirstSection) {
        shouldSwitch = true;
        newIndex = activeIndex - 1;
      }

      if (shouldSwitch) {
        // Only prevent default for moderate scroll speed
        // This allows fast scrolling to work naturally
        if (Math.abs(e.deltaY) < 100) {
          e.preventDefault();
          e.stopPropagation();
        }
        
        scrollCooldown.current = true;
        setIsAnimating(true);
        setActiveIndex(newIndex);
        if (lottieRef.current) {
          if (newIndex === 0) lottieRef.current.playToStart(1200);
          else lottieRef.current.playToEnd(1200);
        }
        setTimeout(() => {
          setIsAnimating(false);
        }, 400);
        setTimeout(() => {
          scrollCooldown.current = false;
        }, 600); // Reduced cooldown time
      }
    };
    document.addEventListener("wheel", handleGlobalWheel, {
      passive: false,
      capture: true,
    });

    return () => {
      document.removeEventListener("wheel", handleGlobalWheel, {
        capture: true,
      });
    };
  }, [isAnimating, isComponentInView, activeIndex, sections.length]);

  // Monitor scroll position to sync active section (backup for fast scrolling)
  useEffect(() => {
    let scrollTimeout;
    
    const checkScrollPosition = () => {
      if (!isComponentInView || scrollCooldown.current) return;
      
      const currentScrollY = window.scrollY || window.pageYOffset;
      const isScrollingUp = currentScrollY < lastScrollY.current;
      lastScrollY.current = currentScrollY;
      
      const componentRect = componentRef.current?.getBoundingClientRect();
      if (componentRect) {
        const scrollProgress = Math.abs(componentRect.top) / componentRect.height;
        
        // Determine which section should be active based on scroll position
        let targetIndex;
        
        if (scrollProgress > 0.4) {
          targetIndex = 1;
        } else {
          targetIndex = 0;
        }
        
        // Only update if different from current
        if (targetIndex !== activeIndex) {
          // If currently at section 02 (index 1) and target is 01 (index 0)
          // Only switch if user is scrolling UP
          if (activeIndex === 1 && targetIndex === 0) {
            if (!isScrollingUp) {
              return; // Don't switch to 01 when scrolling down
            }
          }
          
          setActiveIndex(targetIndex);
          if (lottieRef.current) {
            if (targetIndex === 0) {
              lottieRef.current.playToStart(1800);
            } else {
              lottieRef.current.playToEnd(1800);
            }
          }
        }
      }
    };

    const handleScroll = () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      scrollTimeout = setTimeout(() => {
        checkScrollPosition();
      }, 100);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [isComponentInView, activeIndex]);

  const handleTitleClick = (index) => {
    if (isAnimating || activeIndex === index) return;

    setIsAnimating(true);
    setActiveIndex(index);
    // control lottie: index 0 -> start frame, index 1 -> end frame
    if (lottieRef.current) {
      if (index === 0) lottieRef.current.playToStart(1800);
      else lottieRef.current.playToEnd(1800);
    }
    setTimeout(() => setIsAnimating(false), 400);
  };

  return (
    <div className="fifthPage" id="fifthPage" ref={componentRef}>
      <div className="left">
        <div className="title">Two Solutions for</div>
        <div className="title" style={{ marginTop: 5 }}>
          <span className="black_box">{"{Verifiable Computing}"}</span>
        </div>
        {!isMobile && (
          <div className="sections-container">
            <div className="sections">
              {sections.map((section, index) => (
                <div key={index} className="section-container">
                  <div
                    className={`section-title-wrapper ${
                      activeIndex === index ? "active" : ""
                    }`}
                    onClick={() => handleTitleClick(index)}
                  >
                    <div
                      className={`section_title ${
                        activeIndex === index ? "active" : ""
                      }`}
                    >
                      {section.title}
                    </div>
                  </div>

                  <AnimatePresence>
                    {activeIndex === index && (
                      <motion.div
                        ref={sectionRefs[index]}
                        className="section-content-wrapper scrollbar-hidden"
                        initial={{
                          opacity: 0,
                          height: 0,
                          y: -20,
                        }}
                        animate={{
                          opacity: 1,
                          height: "auto",
                          y: 0,
                        }}
                        exit={{
                          opacity: 0,
                          height: 0,
                          y: 20,
                        }}
                        transition={{
                          duration: 0.4,
                          ease: [0.25, 0.46, 0.45, 0.94],
                        }}
                      >
                        {section.content}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {!isMobile && (
        <div className="lottie-container">
          <ScrollControlledLottie ref={lottieRef} activeIndex={activeIndex} />
        </div>
      )}
      {isMobile && (
        <div>
          <div className="section_title_mobile">
            For Universal Computation: Pico zkVM
          </div>
          <div className="section_content_mobile">
            <div className="des">
              The only modular zkVM achieving 99% real-time proving for current Ethereum blocks
            </div>
            <div className="des" style={{ marginTop: 26 }}>
            From financial calculations to blockchain validation, Pico's modular architecture integrates specialized coprocessors that deliver breakthrough performance. Our distributed multi-GPU clusters prove <b>45M</b> gas Ethereum blocks in <b>6.9</b> seconds on average. With just <b>64</b> GPUs, we offer <b>3.4x</b> better performance efficiency than any competing solution.
            </div>
          </div>
          <div className="section_link_wrapper">
            <div
              className="section_link"
              onClick={() => {
                window.open("https://pico-docs.brevis.network/", "_blank");
              }}
            >
              [ Explore Pico ]
              <img
                alt=""
                className="arrow_right"
                src={arrowRight}
                onClick={() => {}}
              />
            </div>
            <div
              className="section_link"
              onClick={() => {
                window.open("https://ethproofs.org/zkvms/pico", "_blank");
              }}
            >
              [ View on Ethproofs ]
              <img
                alt=""
                className="arrow_right"
                src={arrowRight}
                onClick={() => {}}
              />
            </div>
          </div>
          <div className="stepImg">
            <img src={step1} alt="logo" />
          </div>
          <div className="section_title_mobile">
            For Blockchain Intelligence: ZK Data Coprocessor
          </div>
          <div className="section_content_mobile">
            <div className="des">
              Powering <b>$300M+</b> in live reward programs across <b>20+</b> major protocols.
            </div>
            <div className="des" style={{ marginTop: 26 }}>
              Smart contracts get unlimited memory through our specialized coprocessor that analyzes historical blockchain data off-chain, then provides cryptographic proof of accuracy. PancakeSwap processes millions of users daily with volume-based discounts. Usual distributes rewards across 8 protocols. Linea manages 1 billion token distributions, all with mathematical guarantees of fairness. 
            </div>
          </div>
          <div className="section_link_wrapper">
            <div
              className="section_link"
              onClick={() => {
                window.open(
                  "https://coprocessor-docs.brevis.network/",
                  "_blank"
                );
              }}
            >
              [ Explore zkCoprocessor ]
              <img
                alt=""
                className="arrow_right"
                src={arrowRight}
                onClick={() => {}}
              />
            </div>
          </div>
          <div className="stepImg">
            <img src={step2} alt="logo" />
          </div>
        </div>
      )}
    </div>
  );
}

export default FifthPage;
